import { useSearchParams } from "react-router-dom";

import { Heading } from "@stellar/design-system";

import { ReceiverInviteMessage } from "@/components/ReceiverInviteMessage";
import { SectionHeader } from "@/components/SectionHeader";

import { MULTISIG_FEATURE_FLAGS } from "@/constants/multisigFeatureFlags";

import { useIsMultisigFeatureEnabled } from "@/hooks/useIsMultisigFeatureEnabled";

import {
  SettingsCoSignerSetup,
  SettingsDisableCaptcha,
  SettingsDisableMfa,
  SettingsEnableMemoTracking,
  SettingsEnablePaymentCancellation,
  SettingsEnableReceiverInvitationRetry,
  SettingsEnableReporting,
  SettingsEnableShortLinking,
  SettingsTeamMembers,
  SettingsUnenrollmentBanners,
} from "./components";

import "./styles.scss";

type SettingsTab = "general" | "team" | "recovery";

const TAB_TO_QUERY: Record<SettingsTab, string> = {
  general: "g",
  team: "tm",
  recovery: "ar",
};

const QUERY_TO_TAB: Record<string, SettingsTab> = {
  g: "general",
  tm: "team",
  ar: "recovery",
};

export const Settings = () => {
  // TODO: it also needs to check the user type, only owner and FC allowed to see this
  const isMultisigEnrollmentEnabled = useIsMultisigFeatureEnabled(
    MULTISIG_FEATURE_FLAGS.ENROLLMENT,
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const tabQuery = searchParams.get("tab");
  const activeTab: SettingsTab =
    tabQuery && QUERY_TO_TAB[tabQuery] ? QUERY_TO_TAB[tabQuery] : "general";

  const handleTabChange = (id: SettingsTab) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", TAB_TO_QUERY[id]);
    setSearchParams(newParams);
  };
  const renderTabButton = (
    id: SettingsTab,
    label: string,
    { disabled = false }: { disabled?: boolean } = {},
  ) => {
    const isActive = activeTab === id;
    const classes = ["Settings__tab"];
    if (isActive) classes.push("is-active");
    if (disabled) classes.push("is-disabled");

    return (
      <button
        type="button"
        role="tab"
        aria-selected={isActive}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        className={classes.join(" ")}
        onClick={() => {
          if (!disabled) handleTabChange(id);
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <>
      <SectionHeader>
        <SectionHeader.Row>
          <SectionHeader.Content>
            <Heading as="h2" size="sm">
              Settings
            </Heading>
          </SectionHeader.Content>
        </SectionHeader.Row>
      </SectionHeader>

      <div className="Settings__tabs" role="tablist" aria-label="Settings">
        {renderTabButton("general", "General")}
        {renderTabButton("team", "Team members")}
        {renderTabButton("recovery", "Assisted recovery", { disabled: true })}
      </div>

      {activeTab === "general" ? (
        <div className="CardStack" role="tabpanel">
          {isMultisigEnrollmentEnabled ? <SettingsUnenrollmentBanners /> : null}
          {isMultisigEnrollmentEnabled ? <SettingsCoSignerSetup /> : null}
          <SettingsDisableMfa />
          <SettingsDisableCaptcha />
          <SettingsEnableShortLinking />
          <SettingsEnableMemoTracking />
          <SettingsEnableReporting />
          <SettingsEnablePaymentCancellation />
          <SettingsEnableReceiverInvitationRetry />
          <ReceiverInviteMessage />
        </div>
      ) : null}

      {activeTab === "team" ? (
        <div className="CardStack" role="tabpanel">
          <SettingsTeamMembers />
        </div>
      ) : null}
    </>
  );
};
